/**
 * Pricing Redux Slice
 * State + reducers for pricing domain
 */

import { createSlice, type PayloadAction } from '@ufc/core'
import type { PricedProduct, PricingProfile, PricingState, PricingStatus } from '../domain/types'

const initialState: PricingState = {
  profile: null,
  products: [],
  selectedProduct: null,
  status: 'idle',
  error: null,
  isProfileLoaded: false,
  isProductsLoaded: false,
}

export const pricingSlice = createSlice({
  name: 'pricing',
  initialState,
  reducers: {
    // Data setters (used by loaders)
    setProfile: (state, action: PayloadAction<PricingProfile | null>) => {
      state.profile = action.payload
      state.isProfileLoaded = true
      state.status = 'success'
      state.error = null
    },
    setProducts: (state, action: PayloadAction<PricedProduct[]>) => {
      state.products = action.payload
      state.isProductsLoaded = true
      state.status = 'success'
      state.error = null
    },
    setSelectedProduct: (state, action: PayloadAction<PricedProduct | null>) => {
      state.selectedProduct = action.payload
    },
    setStatus: (state, action: PayloadAction<PricingStatus>) => {
      state.status = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
      state.status = 'error'
    },

    // Action triggers (handled by sagas)
    fetchProfile: (state) => {
      state.status = 'loading'
      state.error = null
    },
    fetchProfileSuccess: (state, action: PayloadAction<PricingProfile>) => {
      state.profile = action.payload
      state.isProfileLoaded = true
      state.status = 'success'
      state.error = null
    },
    fetchProfileFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.status = 'error'
    },

    fetchProducts: (state, _action: PayloadAction<{ categoryCode?: string; search?: string; locale?: string } | undefined>) => {
      state.status = 'loading'
      state.error = null
    },
    fetchProductsSuccess: (state, action: PayloadAction<PricedProduct[]>) => {
      state.products = action.payload
      state.isProductsLoaded = true
      state.status = 'success'
      state.error = null
    },
    fetchProductsFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.status = 'error'
    },

    fetchProduct: (state, _action: PayloadAction<string>) => {
      state.status = 'loading'
      state.error = null
    },
    fetchProductSuccess: (state, action: PayloadAction<PricedProduct | null>) => {
      state.selectedProduct = action.payload
      state.status = 'success'
      state.error = null
    },
    fetchProductFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.status = 'error'
    },

    // Search/filter
    searchProducts: (state, _action: PayloadAction<string>) => {
      state.status = 'loading'
      state.error = null
    },
    filterByCategory: (state, _action: PayloadAction<string>) => {
      state.status = 'loading'
      state.error = null
    },

    // Clear state
    clearPricing: (state) => {
      state.products = []
      state.selectedProduct = null
      state.isProductsLoaded = false
      state.error = null
      state.status = 'idle'
    },
    clearProfile: (state) => {
      state.profile = null
      state.isProfileLoaded = false
    },
    clearError: (state) => {
      state.error = null
    },
  },
})

export const pricingActions = pricingSlice.actions
export const pricingReducer = pricingSlice.reducer
